import React, { useEffect, useState } from 'react'
import CustomCheckboxGroup from 'designer/component/element/CheckboxGroup'
import objectPath from 'object-path'
import {
  getCheckValue,
  getCurrentUi,
  checkDefaultValueByConfig,
} from 'designer/component/action/Data'
import {
  onCheckCondition,
  onUpdateCondition,
} from 'designer/component/action/condition/Action'
import { debounceCallback } from 'utility/debounceCallback.util'
import { getStorage, saveStorage } from 'designer/component/action/Storage'
import { isEmpty } from 'designer/component/action/condition/State'
import useAuth from 'hooks/useAuth'
import { updateCheckboxInput } from './DataControl'
import PropWrapper from './PropWrapper'

const Children = ({ dataProps }) => {
  const { props, designContext, fieldProps, fieldName, fieldKey, errors } =
    dataProps

  const {
    jsonForm,
    isResetData,
    elementAction,
    setFieldFormData,
    setIsInputSearch,
    isUiChanged,
    setIsUiChanged,
  } = designContext
  const { uischema, config } = props
  const { valueType, joinValue } = uischema

  const auth = useAuth()

  const {
    isDynamic = false,
    dynamicKey = '',
    dynamicIndex = 0,
    isArray,
    arrayName,
    arrayKey,
  } = config

  let { uiKey, uiName, data } = fieldProps

  const [isUpdate, setIsUpdate] = useState(false)

  const formData = jsonForm.formData
  let listKey = uischema.connectList
  let listData = objectPath.get(formData, listKey)
  let listUi = getCurrentUi(listKey, jsonForm.schema, jsonForm.uischema)
  let listIdRef = listUi?.loadData?.columnRefId

  const options = uischema?.options || []

  const updateInput = ({ input = {} }, value, isOnChange) => {
    updateValue(input, value, isOnChange)
  }

  const updateValue = async (checkbox, value, isOnChange, valueList) => {
    let { key, name } = checkbox
    if (!key) return
    if (isArray) {
      name = `@${arrayName}.${name}`
      key = `@${arrayName}.${key}`
    }
    updateCheckboxInput({
      ...dataProps,
      value,
      isOnChange,
      uiKey: key,
      uiName: name,
    })
    if (valueList) {
      updateValueList(valueList)
    }
    if (isOnChange) {
      if (isArray) {
        objectPath.set(isUiChanged, `syncData.${arrayKey}`, true)
        setIsUiChanged(isUiChanged)
      }
    }
    saveStorage(jsonForm, uischema, name, value)
  }

  const updateValueList = (valueList) => {
    let checkListKey = `@checkList.${uischema.key}`
    if (isArray) {
      checkListKey = `@${arrayName}.${checkListKey}`
    }
    let checkList = []
    valueList.forEach((name) => {
      let found = options.find((option) => option.name === name)
      if (found) {
        let optionValue = objectPath.get(found, '@value')
        if (optionValue) {
          checkList.push(optionValue)
        }
      }
    })
    setFieldFormData(checkListKey, checkList)
  }

  const updateCheckList = () => {
    if (!listKey) return
    let itemListKey = ''
    let emptyCheck = true
    options.forEach((option) => {
      let { name } = option
      itemListKey = option['@listKey']
      let isChecked = objectPath.get(formData, name)
      if (isChecked) {
        emptyCheck = false
      }
    })
    let listIndex = listData.findIndex(
      (item) => objectPath.get(item, listIdRef) === itemListKey
    )
    objectPath.set(listData, [listIndex, '@isCheckedList'], !emptyCheck)
    setFieldFormData(listKey, listData)
  }

  const updateFreeText = (checkbox, checked, isOnChange) => {
    let { name, valueChecked, valueUnchecked } = checkbox
    let value
    if (isArray) {
      value = objectPath.get(data, `@${arrayName}.${uiName}`)
    } else {
      value = objectPath.get(data, uiKey) || objectPath.get(data, uiName) || ''
    }
    let valueList = value ? value.split(joinValue) : []
    if (checked) {
      if (!valueList.includes(name)) {
        objectPath.push(valueList, '', name)
      }
    } else {
      let index = valueList.findIndex((item) => item === name)
      if (index > -1) {
        objectPath.del(valueList, index)
      }
    }
    value = valueList.join(joinValue)
    updateValue({ key: uiKey, name: uiName }, value, isOnChange, valueList)
    let checkValue = checked ? valueChecked : valueUnchecked
    checkValue = getCheckValue(checkValue)
    updateValue(checkbox, checkValue, isOnChange)
  }

  const [checkboxGroup, setCheckboxGroup] = useState([])

  useEffect(() => {
    let checkboxGroup = []
    options.forEach((checkbox = {}) => {
      let { key, name, valueChecked, valueUnchecked } = checkbox
      let defaultChecked = checkDefaultValueByConfig(
        checkbox.defaultValue,
        key,
        auth,
        jsonForm.formCode
      )
      let value
      if (arrayName) {
        value = objectPath.get(data, `@${arrayName}.${name}`)
      } else {
        value = objectPath.get(data, key) || objectPath.get(data, name)
      }
      checkboxGroup.push(key)
      let checked = undefined
      if (value === undefined) {
        let storage = getStorage(jsonForm, name)
        let checkValue
        if (isEmpty(storage)) {
          checkValue = defaultChecked ? valueChecked : valueUnchecked
        } else {
          checkValue = storage
        }
        checkValue = getCheckValue(checkValue)
        updateValue(checkbox, checkValue)
        if (checkbox?.input && checked) {
          checkboxGroup.push(checkbox.input.key)
          let storage = getStorage(jsonForm, checkbox.input.name)
          let defaultInput = objectPath.get(checkbox, 'input.defaultValue')
          let inputValue
          if (isEmpty(storage)) {
            inputValue = checkDefaultValueByConfig(
              defaultInput,
              checkbox.input.key,
              auth,
              jsonForm.formCode
            )
          } else {
            inputValue = storage
          }
          updateInput(checkbox, inputValue)
        }
      }
    })
    setCheckboxGroup(checkboxGroup)
  }, [JSON.stringify(options)])

  useEffect(() => {
    if (isResetData) {
      options.forEach((checkbox = {}) => {
        let { defaultChecked, valueChecked, valueUnchecked } = checkbox
        let value = defaultChecked ? valueChecked : valueUnchecked
        value = getCheckValue(value)
        updateValue(checkbox, value)
        let defaultValue = objectPath.get(checkbox, 'input.defaultValue')
        updateInput(checkbox, defaultValue)
      })
    }
  }, [isResetData])

  useEffect(() => {
    if (isUpdate) {
      updateCheckList()
      let uiKeyList = [uiKey]
      let conditionKeys = objectPath.get(elementAction, 'conditionKey') || []
      conditionKeys.forEach((keyList = []) => {
        checkboxGroup.forEach((checkboxKey) => {
          if (keyList.includes(checkboxKey)) {
            keyList.forEach((key) => {
              if (!uiKeyList.includes(key)) {
                uiKeyList.push(key)
              }
            })
          }
        })
      })
      debounceCallback(
        onCheckCondition,
        [designContext, data, uiKeyList],
        'on-check-condition'
      )
      if (arrayKey) {
        let syncData = objectPath.get(isUiChanged, 'syncData') || {}
        Object.keys(syncData).forEach((arrayKey) => {
          setIsUiChanged(isUiChanged)
          debounceCallback(
            onUpdateCondition,
            [
              props,
              designContext,
              arrayKey,
              data,
              { isOnChange: isUpdate, isArray, arrayKey },
            ],
            `onInputUpdateArrayCondition-${arrayKey}`
          )
        })
      }
      if (isDynamic) {
        debounceCallback(
          onUpdateCondition,
          [
            props,
            designContext,
            dynamicKey,
            '',
            { isOnChange: isUpdate, isDynamic, dynamicKey, dynamicIndex },
          ],
          `onInputUpdateArrayCondition-${dynamicKey}-${dynamicIndex}`
        )
      }
      setIsUpdate(false)
      debounceCallback(setIsInputSearch, [uiKey], 'onInputUpdate')
    }
  }, [isUpdate])

  return (
    <CustomCheckboxGroup
      fieldName={fieldName}
      fieldKey={fieldKey}
      uischema={uischema}
      data={data}
      updateCheckbox={(checkbox, value, isOnChange) => {
        if (valueType === 'text') {
          updateFreeText(checkbox, value, isOnChange)
        }
        updateValue(checkbox, value, isOnChange)
      }}
      config={config}
      updateInput={updateInput}
      errors={errors}
      isDynamic={isDynamic}
      uiKey={uiKey}
      uiName={uiName}
    />
  )
}

const CheckboxGroupControl = (props) => {
  return (
    <PropWrapper {...props}>
      {({ dataProps }) => <Children dataProps={dataProps} />}
    </PropWrapper>
  )
}

export default CheckboxGroupControl
